import { Divider, Typography } from '@material-ui/core';
import cx from 'classnames';
import { isEmpty } from 'lodash';
import React from 'react';
import { useSelector } from 'react-redux';
import { translate } from '../../../../../common/intl';
import { companyActions, companySelectors } from '../../../../../state/ducks/company';
import { Company } from '../../../../../state/ducks/company/types';
import { AlertDialog } from '../../../../components/dialogs';
import { toastError } from '../../../../components/notifications';
import Text from '../../../../components/Text';
import useActionCreator from '../../../../hooks/useActionCreator';
import useAsync from '../../../../hooks/useAsync';
import useDialog from '../../../../hooks/useDialog';
import DisplayComponent from '../../components/Display';
import EditingComponent from '../../components/Editing';
import SettingsPanel from '../../components/Panels';
import { SettingsPanelProps } from '../../types';
import { useStyles } from './CompanyAddresses.styles';

const ApprovalRequestsSettingsPanel: React.FC<SettingsPanelProps> = (props) => {
  const classes = useStyles();
  const warningDialog = useDialog();
  const companyMine = useSelector(companySelectors.getCompanyMine);
  const [companyAddress, setCompanyAddress] = React.useState(companyMine.addresses);
  const [isEditing, setIsEditing] = React.useState(false);
  const [editingAddressIndex, setEditingAddressIndex] = React.useState(-1);
  const [replacingAddressIndex, setReplacingAddressIndex] = React.useState(-1);
  const [isAddNew, setIsAddNew] = React.useState(false);
  const [newAddress, setNewAddress] = React.useState('');
  const [deleteAddress, setDeleteAddress] = React.useState('');
  const async = useAsync({
    onSuccess: (values?: Company) => {
      setCompanyAddress(values?.addresses ?? []);
    },
    onError: () => {
      setCompanyAddress(companyMine.addresses);
    },
  });
  const changeCompanyAction = useActionCreator(companyActions.updateCompanyMine);

  const saveChanges = (newAddresses) => {
    async.start(changeCompanyAction, { addresses: newAddresses }, async);
  };

  React.useEffect(() => {
    setCompanyAddress(companyMine.addresses);
  }, [companyMine.addresses]);

  const onAddNew = () => {
    setEditingAddressIndex(-1);
    setReplacingAddressIndex(-1);
    setIsAddNew(true);
  };

  const onEdit = (addressIndex: number) => {
    setIsEditing(!isEditing);
    setEditingAddressIndex(addressIndex);
    setReplacingAddressIndex(-1);
    setIsAddNew(false);
  };

  const onReplace = (addressIndex: number) => {
    setEditingAddressIndex(-1);
    setReplacingAddressIndex(addressIndex);
    setIsAddNew(false);
  };

  const onDelete = (address: string) => {
    warningDialog.open();
    setDeleteAddress(address);
  };

  const onSave = () => {
    if (isAddNew && isEmpty(newAddress)) {
      return toastError(translate('administration.company.information.empty.address'));
    }
    warningDialog.open();
  };

  const onDeleteConfirm = () => {
    warningDialog.close();
    const newAddresses = companyAddress.filter(value => value !== deleteAddress);
    setCompanyAddress(newAddresses);
    setDeleteAddress('');
    saveChanges(newAddresses);
  };

  const onEditReplaceSaveConfirm = () => {
    warningDialog.close();
    saveChanges(companyAddress);
    setEditingAddressIndex(-1);
    setReplacingAddressIndex(-1);
  };

  const onAddSaveConfirm = () => {
    setIsAddNew(false);
    warningDialog.close();
    saveChanges([...companyAddress, newAddress]);
    setCompanyAddress(prev => [...prev, newAddress]);
    setNewAddress('');
  };

  const onCancel = () => {
    setIsAddNew(false);
    setIsEditing(!isEditing);
    setEditingAddressIndex(-1);
    setReplacingAddressIndex(-1);
    setNewAddress('');
    warningDialog.close();
    setDeleteAddress('');
    setCompanyAddress(companyMine.addresses);
  };

  function getWarningText () {
    if (isAddNew) {
      return translate('administration.company.information.add.address');
    }
    if (editingAddressIndex >= 0) {
      return translate('administration.company.information.edit.address');
    }
    if (replacingAddressIndex >= 0) {
      return translate('administration.company.information.replace.address');
    }
    if (!isEmpty(deleteAddress)) {
      return translate('administration.company.information.delete.address');
    }
  }

  const onChange = (address: string, index: number) => {
    const newValues = companyAddress.map((value, i) => {
      if (index !== i) {
        return value;
      }
      return address;
    });
    setCompanyAddress(newValues);
  };

  const onAddNewChange = (address: string) => {
    setNewAddress(address);
  };

  const onConfirmDialogClick = () => {
    if (isAddNew) {
      return onAddSaveConfirm();
    }
    if (editingAddressIndex >= 0 || replacingAddressIndex >= 0) {
      return onEditReplaceSaveConfirm();
    }
    if (!isEmpty(deleteAddress)) {
      return onDeleteConfirm();
    }
  };

  return (
    <>
      <SettingsPanel {...props} title={translate('administration.company.information.panel.title.addresses')} {...{ onAddNew }}>
        <Typography variant="h4" className={cx(classes.boldText, classes.marginBottom)}>
          <Text message={translate('administration.company.information.current.addresses')} />
        </Typography>
        {isAddNew && (
          <>
            <EditingComponent
              {...{ onCancel, onSave }}
              onChange={onAddNewChange}
              value="" />
            <Divider className={classes.divider} />
          </>
        )}
        {
          companyAddress.map((address, i) => {
            if (editingAddressIndex === i || replacingAddressIndex === i) {
              return (
                <>
                  <EditingComponent
                    {...{ onSave, onCancel, onChange }}
                    replacingAddress={replacingAddressIndex === i ? address : ''}
                    onChange={(address) => onChange(address, i)}
                    value={editingAddressIndex === i ? address : ''} />
                  <Divider className={classes.divider} />
                </>
              );
            }
            return (
              <>
                <DisplayComponent
                  displayValue={address}
                  onEdit={() => onEdit(i)}
                  onReplace={() => onReplace(i)}
                  onDelete={() => onDelete(address)} />
                <Divider className={classes.divider} />
              </>
            );
          })
        }
      </SettingsPanel>
      <AlertDialog
        handler={warningDialog}
        onConfirm={onConfirmDialogClick}
        onCancel={onCancel}
        confirmLabel={translate('common.ok')}
      >
        {getWarningText()}
      </AlertDialog>
    </>
  );
};

export default ApprovalRequestsSettingsPanel;
